import { Card, DataTable, Badge, Button, Text, List, BlockStack, InlineStack, Divider, Banner } from "@shopify/polaris";

// Static explanation of the MRP engine. Numbers come from the server's MRP_CONFIG
// (passed via /api/mrp/latest) so this page never drifts from the real behaviour.
export function HowItWorks({ config, goToTab }) {
  const horizonDays = config?.HORIZON_DAYS ?? 120;
  const defaultLead = config?.DEFAULT_LEAD_TIME_DAYS ?? 14;
  const pushOutDays = config?.PUSH_OUT_THRESHOLD_DAYS ?? 21;
  const excessMin = config?.EXCESS_MIN_VALUE_EUR ?? 100;
  const retentionDays = config?.RUN_RETENTION_DAYS ?? 90;

  return (
    <BlockStack gap="400">
      <Banner title="In one sentence" tone="info">
        <p>
          Every run answers: <b>what should we order, how much, and by which date</b> — so that no part is missing on the day we build, without buying more than
          the plan needs. It plans on the <b>order book AND the forecast</b>: firm orders always count, and the forecast only adds what exceeds them.
        </p>
      </Banner>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            1. What creates demand
          </Text>
          <Text as="p">Three sources, combined without double counting, over a horizon of {horizonDays} days:</Text>
          <List type="bullet">
            <List.Item>
              <b>Open sales orders</b> (the order book). Every unfulfilled order line counts, due on its delivery term. Machine lines (Greg/Jack) are recognised by
              their PM configuration string and exploded through the exact variant tree; machines that already have a serial number connected are considered in
              production and are not counted again.
            </List.Item>
            <List.Item>
              <b>Open work orders</b>, due on their due date. A work order linked to a sales-order line <i>replaces</i> that portion of the line, so a planned
              build is never counted twice.
            </List.Item>
            <List.Item>
              <b>Forecast</b> (the Forecast tab): expected build quantities per product/platform per month. Per product per month the engine takes{" "}
              <b>the larger of firm orders and forecast</b> — if you forecast 25 Jacks in September and 16 are already ordered, only 9 extra machines of demand
              are added. An empty forecast simply means: plan on the order book alone. <b>Forecast months that have already ended are ignored</b> — a stale
              forecast can never flood the buy list; ignored rows are marked in the Forecast tab and listed as a data-quality note until you roll them forward or
              delete them. The current month still counts. Machine forecasts (Greg/Jack) must specify a structure and representative variant — without one the
              row is ignored and flagged, since it cannot be exploded into parts.
            </List.Item>
          </List>
          <Text as="p" tone="subdued">
            Demand lines with an unreadable delivery term (e.g. “Shipped with next order”) are assumed due today and flagged as a data-quality exception.
          </Text>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            2. What counts as supply
          </Text>
          <List type="bullet">
            <List.Item>
              <b>Stock on hand</b>: warehouse + Trianval, minus the part's safety stock (safety stock is kept aside as buffer, so dipping into it triggers a
              purchase suggestion).
            </List.Item>
            <List.Item>
              <b>Open purchase orders</b> (not cancelled, not received), each with its still-on-order quantity and a receipt date chosen as:{" "}
              <b>confirmed date → expected date → date parsed from the free-text delivery term</b>. POs without any usable date are counted as last-resort supply
              but flagged loudly.
            </List.Item>
            <List.Item>
              <b>Sub-assembly stock at every level</b>: if finished assemblies are on the shelf, their internal parts are not re-ordered — the explosion stops at
              stock.
            </List.Item>
          </List>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            3. How the buy list is calculated
          </Text>
          <List type="number">
            <List.Item>All demand is sorted by need date, earliest first — early orders get the stock, later ones get the shortage.</List.Item>
            <List.Item>
              Each demand consumes stock first, then PO deliveries that arrive on time. A delivery that arrives <i>after</i> the need date is still used, but
              produces an <Badge tone="critical">Expedite</Badge> exception: call the supplier rather than order twice.
            </List.Item>
            <List.Item>Whatever remains explodes down the BOM (or machine tree) until it hits purchase parts: the net requirement.</List.Item>
            <List.Item>
              Net requirements are grouped per week and rounded up by the part's <b>minimum order quantity</b> and <b>order multiple</b>. Surplus from rounding is
              carried forward, so a 50-piece minimum in April automatically covers the weeks after it.
            </List.Item>
            <List.Item>
              Each suggestion gets an <b>order-by date = need date − lead time</b> (default {defaultLead} days when no lead time is set on the part). An order-by
              date in the past shows the red <Badge tone="critical">Late</Badge> badge.
            </List.Item>
          </List>
          <Text as="p" tone="subdued">
            Every line can be traced: the “Driven by” column shows which sales orders, work orders or forecast rows caused the suggestion.
          </Text>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            4. Not every part is planned the same way
          </Text>
          <Text as="p">
            Set the <b>planning method</b> per part on its product page:
          </Text>
          <DataTable
            columnContentTypes={["text", "text"]}
            headings={[<b>Method</b>, <b>Behaviour</b>]}
            rows={[
              ["MRP (default)", "Full time-phased calculation as described above. Use for expensive and long-lead parts: castings, motors, PCBs, housings."],
              [
                "Reorder point",
                "No time-phasing. When on hand + on order drops below the reorder level, one suggestion tops it back up (order-by: today). Use for mid-range predictable parts.",
              ],
              [
                "Kanban / two-bin",
                "Never suggested by the engine — the empty bin on the shop floor is the signal. Use for cheap commodity parts (screws, o-rings, fittings) with generous physical buffers.",
              ],
              ["None", "Ignored entirely (consumables, obsolete parts)."],
            ]}
          />
          <Text as="p" tone="subdued">
            Make-parts (own production) are never on the buy list — their demand explodes into their components instead. Discontinued parts are never suggested,
            but demand on them raises an exception.
          </Text>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            5. The exception radar — often more valuable than the buy list
          </Text>
          <DataTable
            columnContentTypes={["text", "text", "text"]}
            headings={[<b>Exception</b>, <b>Meaning</b>, <b>What to do</b>]}
            rows={[
              [<Badge tone="critical">Release overdue</Badge>, "The order-by date is already in the past.", "Order today; consider asking for expedited delivery."],
              [<Badge tone="critical">Expedite PO</Badge>, "An open PO arrives later than the date its parts are needed.", "Call the supplier to pull the delivery in."],
              [<Badge tone="critical">Overdue receipt</Badge>, "A PO's delivery date has passed and nothing was received.", "Chase the supplier, update the confirmed date."],
              [<Badge tone="attention">No confirmed date</Badge>, "An open PO has no supplier-confirmed delivery date.", "Ask for confirmation and set it on the PO page — MRP can't time-phase without it."],
              [<Badge tone="attention">Missing lead time</Badge>, `A part with demand has no lead time (default ${defaultLead} days is used).`, "Set the real lead time on the product page — the Suppliers tab shows the measured value."],
              [<Badge tone="attention">Missing BOM</Badge>, "A make-part has demand but no components.", "Complete its BOM, otherwise nothing below it gets planned."],
              [<Badge tone="attention">Below reorder point</Badge>, "A reorder-point part dropped below its level.", "Order the suggested top-up quantity."],
              [<Badge tone="info">Push out</Badge>, `A delivery arrives ${pushOutDays}+ days before it is needed (or has no demand at all).`, "Optionally move the delivery later — free cash flow."],
              [<Badge tone="info">Excess stock</Badge>, `More than €${excessMin} of a part remains after all demand in the horizon.`, "No action needed; signals overstock to work off."],
              [<Badge tone="info">Data quality</Badge>, "Unreadable dates, unresolved machine configs, missing suppliers, demand on discontinued parts.", "Fix the underlying data; the message says exactly what and where."],
            ]}
          />
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            6. Clear to build
          </Text>
          <Text as="p">
            For every open work order and machine order, in due-date priority: how many units can be completed <b>right now</b> from stock, how many once{" "}
            <b>confirmed inbound POs</b> (due before the need date) arrive, and which parts are the constraint. Earlier orders reserve stock first, so a later
            order never “steals” parts from an earlier one. Use “Refresh live” for the current situation between runs.
          </Text>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            7. The results are only as good as this data
          </Text>
          <List type="bullet">
            <List.Item>
              <b>Lead times, MOQ, order multiple, safety stock, planning method</b> per part — product page, “Planning (MRP)” section. Stale lead times are the #1
              cause of “we ordered too late”.
            </List.Item>
            <List.Item>
              <b>Confirmed delivery dates</b> on every open PO — PO detail page. The Exceptions tab nags until they're filled.
            </List.Item>
            <List.Item>
              <b>Accurate stock</b> — every movement booked; the suggestions are noise otherwise.
            </List.Item>
            <List.Item>
              <b>Complete BOMs</b> per variant, down to packaging and screws.
            </List.Item>
            <List.Item>
              <b>A maintained forecast</b> for demand beyond the order book — otherwise long-lead parts are only ordered once firm orders exist. Roll it forward
              monthly: add the newest far month, correct the near ones (ended months are ignored automatically, so forgetting to delete them is safe — just
              untidy).
            </List.Item>
          </List>
          <InlineStack gap="200">
            {goToTab && (
              <>
                <Button size="slim" onClick={() => goToTab(5)}>
                  Open Forecast tab
                </Button>
                <Button size="slim" onClick={() => goToTab(6)}>
                  Open Suppliers tab
                </Button>
              </>
            )}
          </InlineStack>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            8. When it runs
          </Text>
          <Text as="p">
            Automatically every night at 04:00, and on demand via “Run now”. Each run is stored (kept {retentionDays} days) so results are comparable over time;
            everything on this page recalculates from live data on each run. Purchase orders themselves are still created in the Aha application — use “Copy
            proposal” or “Export Excel” per supplier on the Buy list, and the suggestions disappear automatically on the next run once the PO exists.
          </Text>
          <Text as="p">
            Every Monday at 06:30 a fresh run is followed by the weekly report e-mail to production: the “To order” list, the “Chase POs” list (open orders that
            are overdue or arriving after production needs them, in priority order) and the full Excel. Preview it any time via “Preview weekly report” at the
            top of this page.
          </Text>
          <Divider />
          <Text as="p" tone="subdued">
            Planning horizon: {horizonDays} days · default lead time: {defaultLead} days · push-out threshold: {pushOutDays} days · excess threshold: €{excessMin}.
          </Text>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
